/**
 * MetaTags Component
 * Dynamic meta tags for SEO optimization
 * Uses React Helmet for managing document head
 */

import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { getMetaDataForLanguage, getHreflangLinks, getCanonicalUrl } from '../../utils/seo-helpers';

interface MetaTagsProps {
  pageName?: string;
}

export const MetaTags: React.FC<MetaTagsProps> = React.memo(({ pageName = 'home' }) => {
  const { language } = useLanguage();

  // Memoize expensive computations
  const metaData = useMemo(
    () => getMetaDataForLanguage(language as 'de' | 'en' | 'es' | 'fr'),
    [language]
  );
  const hreflangLinks = useMemo(() => getHreflangLinks(), []);
  const canonicalUrl = useMemo(
    () => getCanonicalUrl(language as 'de' | 'en' | 'es' | 'fr'),
    [language]
  );

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={language} />
      <title>{metaData.title}</title>
      <meta name="title" content={metaData.title} />
      <meta name="description" content={metaData.description} />
      <meta name="keywords" content={metaData.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* hreflang Tags for International SEO */}
      {hreflangLinks.map((link) => (
        <link
          key={link.hreflang}
          rel={link.rel}
          hrefLang={link.hreflang}
          href={link.href}
        />
      ))}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={metaData.ogTitle} />
      <meta property="og:description" content={metaData.ogDescription} />
      <meta property="og:image" content={metaData.ogImage} />
      <meta property="og:locale" content={language === 'de' ? 'de_DE' : language === 'en' ? 'en_US' : language === 'es' ? 'es_ES' : 'fr_FR'} />
      <meta property="og:site_name" content="Mirolumo" />
      
      {/* Twitter */}
      <meta name="twitter:card" content={metaData.twitterCard} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={metaData.twitterTitle} />
      <meta name="twitter:description" content={metaData.twitterDescription} />
      <meta name="twitter:image" content={metaData.twitterImage} />
      
      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      
      {/* Mobile Optimization */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Mirolumo" />
      
      {/* App Store Meta Tags */}
      <meta name="apple-itunes-app" content="app-id=YOUR_APP_ID" />
      
      {/* Content Type */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* DNS Prefetch */}
      <link rel="dns-prefetch" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
    </Helmet>
  );
});

MetaTags.displayName = 'MetaTags';

export default MetaTags;


